import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  AutocompleteCategory,
  EventItem,
  SearchEventsRequest,
  SearchEventsResponse,
  SearchRequest,
  SearchResponse,
} from '@grpc-clients/external/mercury/v1/entertainment/entertainment.pb';
import { Geolocation } from '@grpc-clients/external/mercury/v1/shared/geolocation.pb';
import { Decimal } from '@grpc-clients/external/mercury/v1/shared/google/type/decimal.pb';
import { Actions, OnInitEffects, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EntertainmentTicketSearchService } from '@pages/entertainment/services/entertainment-ticket-search.service';
import { GenericApiErrorComponent } from '@shared/components/error/generic-api-error/generic-api-error.component';
import { SearchResult } from '@shared/models/search/search-result';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { Card, CardStyleEnum, attributeIdMapper, ribbonTypeMapper } from '@shared/shared/modules/card/models/card';
import { LanguageService } from '@shared/shared/services/language-service';
import { SiteConfigService } from '@shared/shared/services/site-config.service';
import * as UserStore from '@shared/store/user';
import * as moment from 'moment';
import { CookieService } from 'ngx-cookie-service';
import { catchError, combineLatest, filter, map, of, switchMap, take, tap } from 'rxjs';
import {
  GET_TICKETS_PAGINATION_RESULT,
  GET_TICKETS_PAGINATION_RESULT_FAILURE,
  GET_TICKETS_SEARCH_RESULTS,
  GET_TICKETS_SEARCH_RESULTS_FAILURE,
  GET_TICKET_EVENT_RESULT,
  GET_TICKET_EVENT_RESULT_FAILURE,
  GetTicketEventResultFailure,
  GetTicketEventResultSuccess,
  GetTicketsPaginationResult,
  GetTicketsPaginationResultFailure,
  GetTicketsPaginationResultSuccess,
  GetTicketsSearchResults,
  GetTicketsSearchResultsFailure,
  GetTicketsSearchResultsSuccess,
  RESTORE_TICKET_FORM,
  RestoreTicketForm,
  UpdateTicketForm,
} from '../actions';
import {
  GET_TEN_EVENTS,
  GetTenEvents,
  GetTenEventsFailure,
  GetTenEventsSuccess,
} from '../actions/entertainment-search.actions';
import { EntertainmentTicketState, pageCount } from '../reducers';
import { getEntertainmentTicketSearchForm } from '../selectors';

@Injectable()
export class EntertainmentTicketSearchEffects implements OnInitEffects {
  private entertainmentRedesignPhase2: boolean = false;

  constructor(
    private actions$: Actions,
    private cookiesService: CookieService,
    private ticketService: EntertainmentTicketSearchService,
    private snackBar: MatSnackBar,
    private siteSettingsService: SiteSettingsService,
    private store: Store<EntertainmentTicketState>,
    private languageService: LanguageService,
    private siteConfService: SiteConfigService
  ) {}

  public ngrxOnInitEffects(): Action {
    const isAuthenticated$ = this.store.select(UserStore.getUserAuthenticated);
    const siteSettings$ = this.siteSettingsService.fetchSiteSettings();

    combineLatest([isAuthenticated$, siteSettings$])
      .pipe(
        filter(
          ([isAuthenticated, siteSettings]) =>
            !!isAuthenticated && !!siteSettings.id && siteSettings.switches.entertainment_redesign_phase_1_1
        ),
        take(1)
      )
      .subscribe(([, siteSettings]: [boolean, ISiteSettings]) => {
        this.entertainmentRedesignPhase2 = siteSettings?.switches?.entertainment_redesign_phase_2;
        if (window.location.pathname.includes('details')) {
          this.store.dispatch(new GetTenEvents());
        }
      });
    return new RestoreTicketForm();
  }

  public storeTicketsForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GET_TICKETS_SEARCH_RESULTS),
        tap((action: GetTicketsSearchResults) => {
          if (!action.ignoreSearchFormUpdate) {
            const updatedForm = {
              ...action.form,
              event_type: action.form.event_type === undefined ? action.form.object_type : action.form.event_type,
            };
            this.cookiesService.set('entertainmentTicketForm', JSON.stringify(updatedForm), 365, '/');
          }
        })
      ),
    { dispatch: false }
  );

  public restoreTicketForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RESTORE_TICKET_FORM),
      map(() => this.cookiesService.get('entertainmentTicketForm')),
      filter((rawForm) => !!rawForm),
      map((rawForm) => {
        const form = {
          valid: true,
          data: JSON.parse(rawForm),
        };
        form.data.start_date_full = form.data.start_date_full ? new Date(form.data.start_date_full) : null;
        form.data.end_date_full = form.data.end_date_full ? new Date(form.data.end_date_full) : null;

        return new UpdateTicketForm(form);
      })
    )
  );

  public getEntertainmentTicketSearchResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_TICKETS_SEARCH_RESULTS),
      switchMap((action: GetTicketsSearchResults) => {
        const startDate = action.form.start_date_full
          ? { value: moment(action.form.start_date_full).format('YYYY-MM-DD') }
          : null;
        const endDate = action.form.end_date_full
          ? { value: moment(action.form.end_date_full).format('YYYY-MM-DD') }
          : null;
        const eventId = { value: action.form.event_id };

        const searchRequest: SearchRequest.AsObject = {
          startDate: startDate,
          endDate: endDate,
          searchCategories: action.form.search_category,
          languageCode: { value: this.languageService.currentLanguage },
          locationId: +action.form.start_location,
          pageSize: pageCount,
        } as SearchRequest;

        if (action.form.event_type === AutocompleteCategory.EVENT) {
          searchRequest.eventId = eventId;
          searchRequest.startDate = null;
          searchRequest.endDate = null;
        } else if (action.form.event_type === AutocompleteCategory.EVENT_PERFORMER) {
          searchRequest.performerId = eventId;
        } else if (action.form.event_type === AutocompleteCategory.EVENT_VENUE) {
          searchRequest.venueId = eventId;
        }

        const request = new SearchRequest(searchRequest);
        return this.ticketService.search(request).pipe(
          map((data: SearchResponse) => {
            if (this.entertainmentRedesignPhase2) {
              data.results = this.ticketService.mapToResults(data?.results);
              data.extendedResults = this.ticketService.mapToResults(data?.extendedResults);
            }

            return new GetTicketsSearchResultsSuccess(data);
          }),
          catchError(() => of(new GetTicketsSearchResultsFailure()))
        );
      })
    )
  );

  public error$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          GET_TICKETS_SEARCH_RESULTS_FAILURE,
          GET_TICKET_EVENT_RESULT_FAILURE,
          GET_TICKETS_PAGINATION_RESULT_FAILURE
        ),
        tap(() => {
          this.snackBar.openFromComponent(GenericApiErrorComponent, { duration: 5000 });
        })
      ),
    { dispatch: false }
  );

  public getEntertainmentTicketEventResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_TICKET_EVENT_RESULT),
      switchMap(() =>
        combineLatest([
          this.store.select(getEntertainmentTicketSearchForm),
          this.siteSettingsService.fetchSiteSettings(),
        ]).pipe(
          take(1),
          switchMap(([form, siteSettings]) => {
            const request = {
              languageCode: siteSettings.language,
              eventId: form?.data?.event_id,
              eventType: form?.data?.event_type,
            };
            return this.ticketService.getTicketEventResult(request).pipe(
              map((data) => new GetTicketEventResultSuccess(data)),
              catchError(() => of(new GetTicketEventResultFailure()))
            );
          })
        )
      )
    )
  );

  public getEntertainmentPaginationResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_TICKETS_PAGINATION_RESULT),
      switchMap((action: GetTicketsPaginationResult) => {
        return this.ticketService.paginatedResults(action.searchToken, action.pageIndex).pipe(
          map((data) => {
            if (this.entertainmentRedesignPhase2) {
              data.results = this.ticketService.mapToResults(data?.results);
            }
            return new GetTicketsPaginationResultSuccess(data);
          }),
          catchError(() => of(new GetTicketsPaginationResultFailure()))
        );
      })
    )
  );

  public getTenEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_TEN_EVENTS),
      switchMap((action: GetTenEvents) => {
        const browserLocation = this.ticketService.getBrowserGeolocation();
        const defaultGeolocation = this.ticketService.defaultGeolocation();

        let browserCoordinates = null;
        if (browserLocation) {
          browserCoordinates = {
            latitude: new Decimal({ value: browserLocation.lat.toString() }),
            longitude: new Decimal({ value: browserLocation.long.toString() }),
          } as Geolocation.AsObject;
        }

        let requestCount: number;
        switch (action.layout) {
          case 'modern':
            requestCount = 3;
            break;
          case 'default':
            requestCount = 4;
            break;
          default:
            requestCount = 20;
            break;
        }

        const request = new SearchEventsRequest({
          geolocation: browserCoordinates || defaultGeolocation,
          numberOfEvents: requestCount,
          languageCode: this.languageService.currentLanguage,
        });

        return this.ticketService.getTenEvents(request).pipe(
          map((data: SearchEventsResponse) => {
            return new GetTenEventsSuccess(this.mapToEvent(data));
          }),
          catchError(() => of(new GetTenEventsFailure()))
        );
      })
    )
  );

  private mapToEvent(results: SearchEventsResponse) {
    return results?.eventItems.map((item: EventItem) => {
      const componentData = this.siteConfService.getComponentData('searchResults');
      const cardConfig: Card = componentData.default;

      let cardStyle = null;
      let darkTheme = false;

      if (cardConfig) {
        cardStyle = cardConfig.cardStyle || CardStyleEnum.float;
        darkTheme = cardConfig.darkTheme || false;
      }

      const ribbonText = attributeIdMapper[item.attributeIds[0]];
      const ribbonType = ribbonTypeMapper[item.attributeIds[0]];
      const ribbon = ribbonText ? { filled: true, text: ribbonText, type: ribbonType } : null;

      return {
        id: item.eventId,
        cardStyle,
        darkTheme,
        body: {
          heading: item.eventName,
          text: item.venueLocation,
        },
        ribbon,
        location: item.venueLocation,
        imageUrl: item.mainImageURL.value,
        image_url: item.mainImageURL.value,
        attributeIds: item.attributeIds,
        type: 'entertainment',
      } as unknown as SearchResult;
    });
  }
}
